import React, { useState, useEffect } from 'react';
import { useTournament } from '../contexts/TournamentContext';

const LeaderboardModal = ({ isOpen, onClose }) => {
  const { 
    activeCompetitions, 
    selectedCompetition, 
    leaderboard, 
    loadLeaderboard,
    userRank 
  } = useTournament();
  
  const [selectedComp, setSelectedComp] = useState(selectedCompetition);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && selectedComp) {
      loadLeaderboardData();
    }
  }, [isOpen, selectedComp]);

  const loadLeaderboardData = async () => {
    setLoading(true);
    try {
      await loadLeaderboard(selectedComp);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getRankColor = (rank) => {
    if (rank <= 3) return 'text-yellow-400';
    if (rank <= 10) return 'text-green-400';
    return 'text-gray-400';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-atticus-navy-900 to-atticus-navy-800 border border-yellow-400/30 rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-yellow-400 flex items-center gap-2">
            🏆 Tournament Leaderboard
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl transition-colors"
          >
            ×
          </button>
        </div>

        {/* Competition Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Select Tournament
          </label>
          <select
            value={selectedComp || ''}
            onChange={(e) => setSelectedComp(e.target.value)}
            className="w-full bg-atticus-navy-800 border border-yellow-400/30 rounded-lg p-3 text-white focus:outline-none focus:border-yellow-400"
          >
            <option value="">Select a tournament...</option>
            {activeCompetitions.map((comp) => (
              <option key={comp.competition_id} value={comp.competition_id}>
                {comp.title}
              </option>
            ))}
          </select>
        </div>

        {!selectedComp ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-2">Select a tournament to view leaderboard</div>
            <div className="text-sm text-gray-500">Choose from the dropdown above</div>
          </div>
        ) : loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400 mx-auto mb-4"></div>
            <div className="text-gray-400">Loading leaderboard...</div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Tournament Info */}
            {(() => {
              const comp = activeCompetitions.find(c => c.competition_id === selectedComp);
              if (!comp) return null;
              
              return (
                <div className="bg-gradient-to-r from-atticus-navy-800 to-atticus-navy-700 rounded-lg p-4 border border-atticus-navy-600">
                  <h3 className="text-lg font-semibold text-white mb-2">{comp.title}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Type:</span>
                      <span className="text-white ml-2 capitalize">{comp.competition_type}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Capital:</span>
                      <span className="text-white ml-2">{formatCurrency(comp.trading_rules.starting_capital)}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Participants:</span>
                      <span className="text-white ml-2">{comp.participants.length}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Status:</span>
                      <span className="text-green-400 ml-2">{comp.status}</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Leaderboard Table */}
            <div className="bg-gradient-to-br from-atticus-navy-800 to-atticus-navy-700 rounded-lg border border-atticus-navy-600 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-atticus-navy-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Rank
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Trader
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Portfolio Value
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        P&L
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Return %
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Trades
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Win Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-atticus-navy-600">
                    {leaderboard.length > 0 ? (
                      leaderboard.map((entry, index) => (
                        <tr 
                          key={entry.user_id} 
                          className={`hover:bg-atticus-navy-600/50 transition-colors ${
                            entry.user_id === userRank?.user_id ? 'bg-yellow-400/10 border-l-4 border-yellow-400' : ''
                          }`}
                        >
                          <td className="px-4 py-3">
                            <div className={`flex items-center gap-2 ${getRankColor(index + 1)}`}>
                              <span className="font-bold">{getRankIcon(index + 1)}</span>
                              <span className="text-sm">#{index + 1}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">{entry.display_name}</span>
                              {entry.user_id === userRank?.user_id && (
                                <span className="px-2 py-1 text-xs bg-yellow-400/20 text-yellow-400 rounded-full border border-yellow-400/30">
                                  You
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-white font-medium">
                              {formatCurrency(entry.current_balance)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`font-medium ${entry.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {entry.total_pnl >= 0 ? '+' : ''}{formatCurrency(entry.total_pnl)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`font-medium ${entry.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {entry.total_pnl >= 0 ? '+' : ''}{((entry.total_pnl / entry.starting_balance) * 100).toFixed(2)}%
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-gray-300">{entry.total_trades}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-gray-300">{(entry.win_rate || 0).toFixed(1)}%</span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-400">
                          <div className="text-lg mb-2">No participants yet</div>
                          <div className="text-sm">Be the first to join this tournament!</div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* User's Current Position */}
            {userRank && (
              <div className="bg-gradient-to-r from-yellow-400/10 to-yellow-500/10 border border-yellow-400/30 rounded-lg p-4">
                <h4 className="text-yellow-400 font-semibold mb-2 flex items-center gap-2">
                  🎯 Your Current Position
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Rank:</span>
                    <span className="text-yellow-400 ml-2 font-bold">#{userRank.current_rank}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Portfolio:</span>
                    <span className="text-white ml-2 font-medium">{formatCurrency(userRank.current_balance)}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">P&L:</span>
                    <span className={`ml-2 font-medium ${userRank.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {userRank.total_pnl >= 0 ? '+' : ''}{formatCurrency(userRank.total_pnl)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Trades:</span>
                    <span className="text-white ml-2">{userRank.total_trades}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Close Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-atticus-navy-800 text-gray-400 rounded-lg hover:bg-atticus-navy-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardModal; 